import { useCallback, useMemo } from "react";
import { useIsMounted, useUnmountEffect } from "@react-hookz/web";
import usePlayer from "./usePlayer/usePlayer";
import qs from "query-string";
import * as routes from "routes";
import useAuth from "util/useAuth/useAuth";
import useTimer from "./useTimer";
import useLoggedOutLiveTracker from "shared/user/lives/LoggedOutLiveTrackerContext/useLoggedOutLivesTracker";
import { isLoggedIn } from "util/useAuth/types";
import getDailyLivesUsed from "shared/user/lives/getDailyLivesUsed";
import api, { isSuccess } from "util/api";
import {
  apiDailyMinutesLogMinuteUsedPath,
  ApiDailyMinutesLogMinuteUsedResponse,
} from "apiRoutes/apiDailyMinuteRoutes";
import useLocalStorage from "util/useLocaleStorage";
import { useLayer, UseLayerArrowProps, Arrow, LayerProps } from "react-laag";
import { AnimatePresence } from "framer-motion";
import useOutOfLivesCallback from "shared/user/lives/useOutOfLivesCallback";
import outOfLives from "shared/user/lives/outOfLives";
import { playerToSearchParams } from "./usePlayer/getDefaultExternalState";
import { motion } from "framer-motion";
import * as animation from "ui/organisms/GameSelector/animationConsts";
import {
  CheckCircleIcon,
  CogIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import classNames from "classnames";
import { PlayerExternalState } from "./usePlayer/useExternalState/useExternalState";

interface Props {
  defaultState?: Partial<PlayerExternalState>;
}

export default function useVocalDriller(props?: Props) {
  const auth = useAuth();
  const player = usePlayer({ defaultState: props?.defaultState });
  const { markLifeLost: loggedOutMarkLifeLost } = useLoggedOutLiveTracker();
  const livesUsed = getDailyLivesUsed(auth);
  const [recentlyUsedMelodyShortHands, setRecentlyUsedMelodyShortHands] =
    useLocalStorage<string[]>("recently-used-melody-short-hands", [], "local");
  const storeShorthandToRecentlyUsed = useCallback(() => {
    return () => {
      const recentlyUsedSet = new Set(recentlyUsedMelodyShortHands);
      const currentMelodyShorthand = player.state.shorthands?.melody?.trim();

      if (!currentMelodyShorthand) {
        return;
      }

      if (recentlyUsedSet.has(currentMelodyShorthand)) {
        recentlyUsedSet.delete(currentMelodyShorthand);
      }

      const updatedRecentlyUsed = [
        currentMelodyShorthand,
        ...Array.from(recentlyUsedSet),
      ].slice(0, 10);

      setRecentlyUsedMelodyShortHands(updatedRecentlyUsed);
    };
  }, [
    player.state.shorthands?.melody,
    recentlyUsedMelodyShortHands,
    setRecentlyUsedMelodyShortHands,
  ]);

  const onMinuteChange = useCallback(async () => {
    if (isLoggedIn(auth)) {
      try {
        const user = await api<ApiDailyMinutesLogMinuteUsedResponse>(
          apiDailyMinutesLogMinuteUsedPath,
          "POST"
        );

        if (isSuccess(user)) {
          auth.replaceUser(user.response);

          return;
        }
      } catch (e) {
        console.error(e);
      }

      alert("Something went wrong. Please try again later.");
    } else {
      loggedOutMarkLifeLost();
    }
  }, [auth, loggedOutMarkLifeLost]);

  const timer = useTimer({
    minutesFromStorage: livesUsed,
    onMinuteChange,
  });

  useOutOfLivesCallback({ auth, callback: player.stop });

  useUnmountEffect(() => {
    player.stop();
    timer.stop();
  });

  const isMounted = useIsMounted();
  const locked = outOfLives(auth);
  const playButtonHelpContextIsOpen =
    !player.enabled.enabled && isMounted() && !locked;
  const { renderLayer, triggerProps, layerProps, arrowProps } = useLayer({
    isOpen: playButtonHelpContextIsOpen,
    overflowContainer: true,
    auto: true,
    snap: true,
    triggerOffset: 12, // keep some distance to the input
    containerOffset: 16, // keep some distance to the containers edges
    arrowOffset: 8, // give the arrow some room also
    placement: "bottom-end",
    possiblePlacements: ["bottom-end", "top-end"],
  });

  const url = qs.stringifyUrl({
    url: routes.singing.vocalDriller,
    query: { ...playerToSearchParams(player.state) },
  });

  const playButtonProps = useMemo(() => {
    return {
      ...triggerProps,
      disabled: !player.enabled.enabled,
      active: player.state.state === "playing",
      onClick: () => {
        if (player.state.state === "playing") {
          player.stop();
          timer.stop();
        } else {
          player.start();
          timer.start();
          storeShorthandToRecentlyUsed();
        }
      },
    };
  }, [player, storeShorthandToRecentlyUsed, timer, triggerProps]);

  const playButtonTooltip =
    playButtonHelpContextIsOpen &&
    renderLayer(
      <AnimatePresence>
        {!player.enabled.enabled && isMounted() && (
          <PlayButtonHelpContext
            layerProps={layerProps}
            arrowProps={arrowProps}
            enabled={player.enabled}
          />
        )}
      </AnimatePresence>
    );

  return {
    url,
    timer,
    player,
    playButtonProps,
    playButtonTooltip,
    locked,
    recentlyUsedMelodyShortHands,
  };
}

function PlayButtonHelpContext({
  arrowProps,
  layerProps,
  enabled,
}: {
  arrowProps: UseLayerArrowProps;
  layerProps: LayerProps;
  enabled: ReturnType<typeof usePlayer>["enabled"];
}) {
  const iconClassName = "h-5 mr-1";
  const passIcon = (
    <CheckCircleIcon
      className={classNames(iconClassName, "text-emerald-500")}
    />
  );
  const failIcon = (
    <XCircleIcon className={classNames(iconClassName, "text-red-500")} />
  );

  return (
    <motion.div
      {...layerProps}
      className="bg-black text-white p-3 border-2 border-white shadow-xl rounded z-10"
      animate={animation.animate}
      initial={animation.initial}
      exit={animation.exit}
    >
      {!enabled.breakdown.loaded && (
        <motion.div
          className={"flex items-center"}
          animate={animation.animate}
          initial={animation.initial}
          exit={animation.exit}
        >
          <CogIcon className={"h-5 animate-spin mr-1"} /> Loading
        </motion.div>
      )}
      <motion.div
        className={"flex items-center"}
        animate={animation.animate}
        initial={animation.initial}
        exit={animation.exit}
      >
        {enabled.breakdown.range ? passIcon : failIcon} Range Selected
      </motion.div>
      <motion.div
        className={"flex items-center"}
        animate={animation.animate}
        initial={animation.initial}
        exit={animation.exit}
      >
        {enabled.breakdown.melodyShorthand ? passIcon : failIcon} Melody
        Selected
      </motion.div>
      <Arrow {...(arrowProps as any)} />
      {/*NO idea why this type isn't working but it is copy/paste from docs*/}
    </motion.div>
  );
}
